import { useEffect } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useCurrentUserEmail } from '../catalog/microapps/hooks/useCurrentUserEmail';

/**
 * CSS selector for the "All" filter option in the scaffolder OwnerListPicker
 * component. Verified against Backstage v1.46.0 — re-verify this selector
 * after any Backstage upgrade, as data-testid attributes are not part of the
 * stable public API and may change without a semver breaking-change notice.
 */
const ALL_FILTER_SELECTOR = '[data-testid="owner-picker-all"]';

/** Unique ID for the injected style element so we can clean it up. */
const STYLE_ELEMENT_ID = 'task-owner-filter-restriction-style';

function injectHideStyle() {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `${ALL_FILTER_SELECTOR} { display: none !important; }`;
  document.head.appendChild(style);
}

function removeHideStyle() {
  document.getElementById(STYLE_ELEMENT_ID)?.remove();
}

/**
 * UX-only component that hides the "All" filter option on the scaffolder tasks
 * list page (/create/tasks) for users not in the configured allowlist.
 *
 * ⚠️  SECURITY NOTE: This is a UX simplification, NOT a security control.
 * Hiding a DOM element via CSS can be trivially bypassed using browser DevTools.
 * Do NOT rely on this component for access control or data protection.
 * The Backstage permissions framework must be used if backend enforcement is
 * required.
 *
 * The allowlist is driven by app-config.yaml
 * (scaffolder.tasksAllFilterAllowedEmails) so that access can be updated
 * without a code change or redeployment.
 *
 * This component renders nothing to the DOM (returns null).
 */
export const TaskOwnerFilterRestriction = () => {
  const config = useApi(configApiRef);
  const currentUserEmail = useCurrentUserEmail();

  // Inject the hide-style immediately on mount so the "All" filter is never
  // briefly visible while the current user's email is still loading.
  useEffect(() => {
    injectHideStyle();
    return removeHideStyle;
  }, []);

  // Once the email has resolved, reveal the filter for allowed users only.
  useEffect(() => {
    if (!currentUserEmail) {
      // Email still loading — keep the filter hidden.
      return;
    }

    const allowedEmails =
      config.getOptionalStringArray('scaffolder.tasksAllFilterAllowedEmails') ??
      [];

    const isAllowed = allowedEmails.some(
      email => email.toLowerCase() === currentUserEmail.toLowerCase(),
    );

    if (isAllowed) {
      removeHideStyle();
    }
    // If not allowed, the style injected on mount stays in place — no action.
  }, [currentUserEmail, config]);

  return null;
};
